import { useState } from "react";
import { Loader2 } from "@fg/icons";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { mailApi, type MessageSummary } from "@/api/mailApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useBulkMessageAction } from "@/api/hooks";

type Mode = "all" | "keep-latest" | "older-10";
const PAGE = 200;
/** A sweep looks at most this many messages from one sender: more than that deserves a rule, not a click. */
const LIMIT = 2000;

const MODES: Array<{ id: Mode; label: string; hint: string }> = [
    { id: "all", label: "Delete all messages from this sender", hint: "Everything they ever sent you." },
    { id: "keep-latest", label: "Keep only the latest message", hint: "Clears the backlog and keeps the newest one." },
    { id: "older-10", label: "Delete messages older than 10 days", hint: "Recent mail stays." }
];

/** Outlook's Sweep: clean up everything from one sender in one go, instead of deleting message by message. */
export function SweepDialog({ senderEmail, senderName, open, onOpenChange }: {
    senderEmail: string;
    senderName: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const token = useAccessToken();
    const bulk = useBulkMessageAction();
    const [mode, setMode] = useState<Mode>("keep-latest");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<string | null>(null);

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const found: MessageSummary[] = [];
            for (let skip = 0; skip < LIMIT; skip += PAGE) {
                const page = await mailApi.search({ from: senderEmail, take: PAGE, skip }, token);
                found.push(...page.messages.filter((m) => m.from.email.toLowerCase() === senderEmail.toLowerCase()));
                if (page.messages.length < PAGE) break;
            }
            // Newest first, then pick what goes.
            found.sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt));
            const cutoff = Date.now() - 10 * 86_400_000;
            const victims = mode === "all"
                ? found
                : mode === "keep-latest"
                    ? found.slice(1)
                    : found.filter((m) => Date.parse(m.receivedAt) < cutoff);
            // Flagged and pinned mail is never swept away by accident.
            const ids = victims.filter((m) => !m.isFlagged && !m.isPinned).map((m) => m.id);
            if (ids.length === 0) {
                setResult("Nothing to sweep: no unflagged messages match.");
            } else {
                await bulk.mutateAsync({ action: "trash", ids });
                setResult(`Moved ${ids.length} message${ids.length === 1 ? "" : "s"} to Deleted Items. Flagged and pinned mail was kept.`);
            }
        } catch {
            setResult("Couldn't finish the sweep. Nothing more was moved; try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) setResult(null); } }}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Sweep {senderName || senderEmail}</DialogTitle>
                    <DialogDescription>{senderEmail}. Moves mail to Deleted Items, where you can still restore it.</DialogDescription>
                </DialogHeader>
                <div role="radiogroup" aria-label="What to sweep" className="space-y-1.5">
                    {MODES.map((m) => (
                        <label key={m.id} className="flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 text-sm hover:bg-accent/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                            <input type="radio" name="sweep-mode" checked={mode === m.id} onChange={() => setMode(m.id)} className="mt-1" />
                            <span><span className="block font-medium">{m.label}</span><span className="block text-xs text-muted-foreground">{m.hint}</span></span>
                        </label>
                    ))}
                </div>
                {result ? <p role="status" className="text-sm">{result}</p> : null}
                <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>{result ? "Close" : "Cancel"}</Button>
                    {result ? null : (
                        <Button type="button" onClick={() => void run()} disabled={busy}>
                            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Sweep
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
